﻿/**
 * 内部审计门户 · 登录页（与 UserProvider 解耦，仅接收 login 回调）
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  buildMobileH5LoginUrl,
  isLikelyMobilePhoneBrowser,
  redirectToMobileH5EntryIfNeeded,
  setPreferDesktopPortalSession,
  syncPreferDesktopFromUrl,
} from '../../utils/mobileH5Portal.js';
import {
  User,
  Lock,
  LogIn,
  Eye,
  EyeOff,
  X,
  Sparkles,
  Shield,
  AlertTriangle,
} from 'lucide-react';

const DESKTOP_REMEMBER_KEY = 'audit_portal_desktop_remember_login';
const DESKTOP_SAVED_USER_KEY = 'audit_portal_desktop_saved_username';
const DESKTOP_SAVED_PASS_KEY = 'audit_portal_desktop_saved_password';
const DESKTOP_LAST_ACCOUNT_KEY = 'audit_portal_desktop_last_account';

function normalizeLoginAccount(value) {
  return String(value || '').trim();
}

export function AuditPortalLoginPage({ login, onSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberUsername, setRememberUsername] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [sessionConflict, setSessionConflict] = useState(null);

  const h5LoginUrl = useMemo(() => buildMobileH5LoginUrl(), []);
  const showMobileH5Hint = useMemo(() => isLikelyMobilePhoneBrowser(), []);

  useEffect(() => {
    syncPreferDesktopFromUrl();
    redirectToMobileH5EntryIfNeeded();
  }, []);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('audit_portal_demo_error');
      if (msg) {
        setError(msg);
        sessionStorage.removeItem('audit_portal_demo_error');
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      const rememberRaw = localStorage.getItem(DESKTOP_REMEMBER_KEY);
      const remember = rememberRaw == null ? true : rememberRaw === '1';
      localStorage.removeItem(DESKTOP_SAVED_PASS_KEY);
      setRememberUsername(remember);
      if (!remember) return;

      const savedUser = normalizeLoginAccount(localStorage.getItem(DESKTOP_SAVED_USER_KEY));
      const lastAccount = normalizeLoginAccount(localStorage.getItem(DESKTOP_LAST_ACCOUNT_KEY));
      let cachedAccount = '';
      try {
        const cachedUser = JSON.parse(localStorage.getItem('audit_portal_user') || 'null');
        cachedAccount = normalizeLoginAccount(cachedUser?.username || cachedUser?.name);
      } catch (_) {}

      const nextAccount = savedUser || lastAccount || cachedAccount;
      if (nextAccount) setUsername(nextAccount);
    } catch (_) {}
  }, []);

  const persistRememberCredentials = useCallback((enabled, user) => {
    try {
      const account = normalizeLoginAccount(user);
      localStorage.removeItem(DESKTOP_SAVED_PASS_KEY);
      if (account) localStorage.setItem(DESKTOP_LAST_ACCOUNT_KEY, account);
      if (enabled) {
        localStorage.setItem(DESKTOP_REMEMBER_KEY, '1');
        if (account) localStorage.setItem(DESKTOP_SAVED_USER_KEY, account);
      } else {
        localStorage.setItem(DESKTOP_REMEMBER_KEY, '0');
        localStorage.removeItem(DESKTOP_SAVED_USER_KEY);
      }
    } catch (_) {}
  }, []);

  const runLogin = useCallback(
    async ({ forceNewSession = false } = {}) => {
      const account = normalizeLoginAccount(username);
      setError('');
      setSessionConflict(null);
      setLoading(true);
      try {
        const result = await login(account, password, { forceNewSession });
        if (!forceNewSession && !result.success && result.code === 'SESSION_CONFLICT') {
          setSessionConflict({
            message:
              result.error ||
              '该账号已在其他终端登录（含桌面端与浏览器）。是否强制登录并退出其他端？',
          });
          return;
        }
        if (result && result.success) {
          persistRememberCredentials(rememberUsername, account);
          try {
            onSuccess?.();
          } catch (cbErr) {
            console.error('[LoginPage] onSuccess', cbErr);
            setError(cbErr?.message || '登录后处理失败，请刷新重试');
          }
        } else {
          setError((result && result.error) || '登录失败');
        }
      } catch (err) {
        console.error('[LoginPage] login', err);
        setError(err?.message || '登录失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    },
    [login, username, password, rememberUsername, persistRememberCredentials, onSuccess]
  );

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      await runLogin();
    },
    [runLogin]
  );

  const goDemo = () => {
    setError('');
    setDemoLoading(true);
    const path = window.location.pathname || '/';
    const q = new URLSearchParams(window.location.search);
    q.set('demo', '1');
    window.location.assign(`${path}?${q.toString()}${window.location.hash || ''}`);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-stretch justify-center font-sans selection:bg-[var(--theme-primary)]/30 selection:text-white overflow-hidden">
      {/* 背景 */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(59,130,246,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(99,102,241,0.08),transparent_45%)]" />
        <div
          className="absolute -top-[35%] -left-[15%] w-[65%] h-[65%] rounded-full blur-[100px] opacity-30 animate-[pulse_10s_ease-in-out_infinite]"
          style={{ background: 'var(--theme-primary)' }}
        />
        <div
          className="absolute bottom-[-25%] right-[-10%] w-[55%] h-[55%] rounded-full blur-[90px] opacity-20 animate-[pulse_12s_ease-in-out_infinite]"
          style={{ background: 'var(--theme-primary-light)', animationDelay: '2s' }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] opacity-40" />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto flex flex-col lg:flex-row min-h-screen lg:min-h-0 lg:my-auto lg:max-h-[min(900px,92vh)] lg:rounded-2xl lg:overflow-hidden lg:shadow-2xl border border-white/[0.06] lg:border-white/10">
        {/* 左侧品牌区（大屏） */}
        <div
          className="hidden lg:flex lg:w-[42%] flex-col justify-between p-10 text-white relative overflow-hidden"
          style={{
            background: 'linear-gradient(165deg, var(--theme-gradient-from) 0%, #0f172a 55%, #020617 100%)',
          }}
        >
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-xl blur-md opacity-50"
                  style={{ background: 'var(--theme-primary)' }}
                />
                <div className="relative w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                  <Shield className="w-7 h-7 text-white/90" aria-hidden />
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Internal Audit</p>
                <h2 className="text-xl font-semibold tracking-tight">内部审计门户</h2>
              </div>
            </div>
            <h3 className="text-2xl font-bold leading-snug mb-4 text-white/95">
              统一入口
              <br />
              <span className="text-white/70 font-normal text-lg">项目管理 · 整改 · 风险与制度协同</span>
            </h3>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300/90" aria-hidden />
                <span>与浏览器访问同一站点，数据实时一致</span>
              </li>
              <li className="flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300/90" aria-hidden />
                <span>登录会话由服务端校验，请勿在公共设备保存密码</span>
              </li>
            </ul>
          </div>
          <p className="relative z-10 text-xs text-white/40">© 内部审计门户 · 仅限授权人员使用</p>
        </div>

        {/* 右侧表单区 */}
        <div className="flex-1 flex flex-col justify-center p-6 sm:p-10 bg-slate-900/80 lg:bg-slate-900/85 min-h-screen lg:min-h-0">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md mx-auto"
          >
            {/* 小屏标题 */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center overflow-hidden">
                <Shield className="w-6 h-6 text-white/90" aria-hidden />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">内部审计门户</h1>
                <p className="text-sm text-slate-400">欢迎登录</p>
              </div>
            </div>

            {showMobileH5Hint && (
              <div className="mb-6 rounded-2xl border border-indigo-400/25 bg-indigo-500/10 px-4 py-3.5 space-y-3">
                <p className="text-sm text-indigo-100/95 leading-snug">
                  手机访问推荐跳转至 <span className="font-semibold text-white">移动端 H5 门户</span>（站点路径{' '}
                  <code className="text-xs bg-black/20 px-1.5 py-0.5 rounded">/m/</code>
                  ，与当前站点同源）。
                </p>
                <a
                  href={h5LoginUrl}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-900/30 active:scale-[0.99] transition-transform"
                >
                  前往移动端 H5 登录
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setPreferDesktopPortalSession(true);
                    window.location.reload();
                  }}
                  className="w-full text-center text-xs text-slate-400 underline underline-offset-2 hover:text-slate-200"
                >
                  仍在本页使用桌面版登录
                </button>
              </div>
            )}

            <div className="hidden lg:block mb-8">
              <h1 className="text-2xl font-bold text-white">登录账户</h1>
              <p className="text-slate-400 text-sm mt-1">使用管理员分配的账号、用户名与密码</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" noValidate>
              {error && (
                <div
                  role="alert"
                  className="p-4 bg-red-500/10 border border-red-500/25 rounded-xl text-red-100 text-sm flex gap-3 items-start"
                >
                  <div className="p-1 bg-red-500/20 rounded-md shrink-0 mt-0.5">
                    <X className="w-4 h-4 text-red-300" aria-hidden />
                  </div>
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="audit-login-user" className="block text-sm font-medium text-slate-300 mb-2">
                  账号 / 用户名
                </label>
                <div className="relative">
                  <User
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none"
                    aria-hidden
                  />
                  <input
                    id="audit-login-user"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 bg-slate-950/60 border border-slate-700/60 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]/50 focus:border-[var(--theme-primary)]/40 transition-shadow"
                    placeholder="请输入账号或用户名"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="audit-login-pass" className="block text-sm font-medium text-slate-300 mb-2">
                  密码
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none"
                    aria-hidden
                  />
                  <input
                    id="audit-login-pass"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-12 py-3.5 bg-slate-950/60 border border-slate-700/60 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]/50 focus:border-[var(--theme-primary)]/40 transition-shadow"
                    placeholder="请输入密码"
                    required
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                    aria-label={showPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={rememberUsername}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setRememberUsername(on);
                    if (!on) persistRememberCredentials(false, username);
                  }}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-950/60 text-[var(--theme-primary)] focus:ring-[var(--theme-primary)]/50"
                />
                <span className="text-sm text-slate-300 group-hover:text-slate-200">
                  记住账号 / 用户名（不保存密码）
                </span>
              </label>
              <p className="text-[11px] text-slate-500 leading-relaxed -mt-2">
                为降低泄露风险，系统不会在浏览器本地保存密码。
              </p>

              <button
                type="submit"
                disabled={loading || demoLoading}
                className="w-full py-3.5 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 focus-visible:ring-[var(--theme-primary)] transition-transform active:scale-[0.99]"
                style={{
                  background: 'linear-gradient(135deg, var(--theme-gradient-from), var(--theme-gradient-to))',
                  boxShadow: '0 8px 24px var(--theme-primary-bg)',
                }}
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5" aria-hidden />
                    登录系统
                  </>
                )}
              </button>
            </form>

            <details className="mt-8 group border-t border-white/10 pt-6">
              <summary className="text-center text-xs text-slate-500 cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-center gap-1 hover:text-slate-400 transition-colors">
                <Sparkles className="w-3.5 h-3.5 text-amber-400/80" aria-hidden />
                访客演示与本地说明
                <span className="text-[10px] opacity-70">（点击展开）</span>
              </summary>
              <div className="mt-4 space-y-3">
                <button
                  type="button"
                  disabled={loading || demoLoading}
                  onClick={goDemo}
                  className="w-full py-3 rounded-xl font-medium border border-amber-500/35 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                >
                  {demoLoading ? (
                    <span className="w-5 h-5 border-2 border-amber-200/30 border-t-amber-200 rounded-full animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 text-amber-300" aria-hidden />
                      访客演示（无需账号）
                    </>
                  )}
                </button>
                <p className="text-center text-[11px] text-slate-500 leading-relaxed">
                  本地开发请先运行 <code className="px-1 py-0.5 rounded bg-slate-800/80 text-slate-300">npm run dev:web</code>
                  ，在 <code className="px-1 py-0.5 rounded bg-slate-800/80 text-slate-300">.env</code> 中开启{' '}
                  <code className="px-1 py-0.5 rounded bg-slate-800/80 text-slate-300">ALLOW_PUBLIC_DEMO</code>
                  ；详见 env.example
                </p>
              </div>
            </details>
          </motion.div>
        </div>
      </div>

      {sessionConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="audit-session-conflict-title"
            className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/40"
          >
            <div className="flex items-start gap-3 border-b border-white/10 px-5 py-4">
              <div className="mt-0.5 rounded-xl border border-amber-300/30 bg-amber-400/10 p-2">
                <AlertTriangle className="h-5 w-5 text-amber-300" aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 id="audit-session-conflict-title" className="text-base font-semibold text-white">
                  账号已在其他端登录
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-300">{sessionConflict.message}</p>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setSessionConflict(null)}
                disabled={loading}
                className="rounded-xl border border-slate-600 px-4 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5 disabled:opacity-60"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => runLogin({ forceNewSession: true })}
                disabled={loading}
                className="rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, var(--theme-gradient-from), var(--theme-gradient-to))',
                }}
              >
                {loading ? '正在登录...' : '强制登录'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
